import { Box, IconButton, Tooltip, useToast } from '@chakra-ui/react';
import React, { useCallback } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';
import { generateXlsx2CsvConfig } from '@finley-technologies/tape-builder';

import { FiDroplet } from 'react-icons/fi';

const defaultToastOptions = {
  position: 'top' as const,
  isClosable: true,
  duration: 5000,
} as const;

type ExportXlsx2CsvConfigButtonProps = {
  creditAgreementFullArtifact: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  creditFacilitySlug: string;
};
export const ExportXlsx2CsvConfigButton = ({
  creditAgreementFullArtifact,
  creditFacilitySlug,
}: ExportXlsx2CsvConfigButtonProps) => {
  const toast = useToast(defaultToastOptions);

  const onError = useCallback(
    (error: any) => {
      // eslint-disable-next-line no-console
      console.error('onError:', error);
      const message = `${error}`;
      toast({
        id: `${Date.now()}`,
        position: 'top' as const,
        isClosable: true,
        title: message,
        status: 'error',
        duration: null, // errors are important, do not auto-close
      });
    },
    [toast],
  );

  const downloadXlsx2CsvConfig = (
    fullArtifact: CreditAgreement.DomainEntity.CreditAgreementFullArtifact,
  ) => {
    try {
      const output = generateXlsx2CsvConfig({
        calcAgentMechanics:
          creditAgreementFullArtifact.borrowingBaseMechanics.calcAgentMechanics,
        parties: creditAgreementFullArtifact.facilityMechanics.parties,
      });
      const json = JSON.stringify(output);
      const blob = new Blob([json], { type: 'application/json' });
      const href = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const nodeEnv = commonUtils.EnvUtils.getEnv();
      link.href = href;
      const envOnStage = nodeEnv === 'prod' ? '-' : `-${nodeEnv}-`;
      link.download = `${creditFacilitySlug}${envOnStage}version${fullArtifact.version}-xlsx2csv.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      onError(error);
    }
  };
  return (
    <Box display="flex" justifyContent="flex-end">
      <Tooltip label="Export config for xlsx2csv">
        <IconButton
          aria-label="Export config for xlsx2csv"
          icon={<FiDroplet />}
          onClick={() => downloadXlsx2CsvConfig(creditAgreementFullArtifact)}
          variant="ghost"
        />
      </Tooltip>
    </Box>
  );
};
